'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import BookingStepIndicator from './BookingStepIndicator';
import EventTypeSelector from './EventTypeSelector';
import EventDetailsForm from './EventDetailsForm';
import MenuSelectionForm from './MenuSelectionForm';
import ContactInformationForm from './ContactInformationForm';
import BookingSummary from './BookingSummary';
import ConfirmationMessage from './ConfirmationMessage';
import { createEventBooking, type EventBooking } from '@/services/supabaseService';

interface EventDetails {
  date: string;
  time: string;
  duration: string;
  guestCount: string;
  location: string;
  address: string;
  postalCode: string;
  city: string;
}

interface ContactInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  preferredContact: string;
  newsletter: boolean;
}

export default function BookingInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedEventType, setSelectedEventType] = useState('');
  const [eventDetails, setEventDetails] = useState<EventDetails>({
    date: '',
    time: '',
    duration: '',
    guestCount: '',
    location: '',
    address: '',
    postalCode: '',
    city: '',
  });
  const [selectedPackage, setSelectedPackage] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [dietaryNeeds, setDietaryNeeds] = useState<string[]>([]);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    preferredContact: '',
    newsletter: false,
  });
  const [eventDetailsErrors, setEventDetailsErrors] = useState<Partial<Record<keyof EventDetails, string>>>({});
  const [contactInfoErrors, setContactInfoErrors] = useState<Partial<Record<keyof ContactInfo, string>>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const steps = [
    'Veranstaltungsart',
    'Details',
    'Menü',
    'Kontakt',
    'Zusammenfassung',
  ];

  const validateEventDetails = (): boolean => {
    const errors: Partial<Record<keyof EventDetails, string>> = {};

    if (!eventDetails.date) errors.date = 'Bitte wählen Sie ein Datum';
    if (!eventDetails.time) errors.time = 'Bitte wählen Sie eine Uhrzeit';
    if (!eventDetails.duration) errors.duration = 'Bitte wählen Sie die Dauer';
    if (!eventDetails.guestCount) {
      errors.guestCount = 'Bitte geben Sie die Anzahl der Gäste an';
    } else if (parseInt(eventDetails.guestCount) < 0) {
      errors.guestCount = 'Mindestens 0 Gäste erforderlich';
    }
    if (!eventDetails.location) errors.location = 'Bitte geben Sie den Veranstaltungsort an';
    if (!eventDetails.address) errors.address = 'Bitte geben Sie die Adresse an';
    if (!eventDetails.postalCode) {
      errors.postalCode = 'Bitte geben Sie die Postleitzahl an';
    } else if (!/^\d{5}$/.test(eventDetails.postalCode)) {
      errors.postalCode = 'Ungültige Postleitzahl';
    }
    if (!eventDetails.city) errors.city = 'Bitte geben Sie die Stadt an';

    setEventDetailsErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateContactInfo = (): boolean => {
    const errors: Partial<Record<keyof ContactInfo, string>> = {};

    if (!contactInfo.firstName) errors.firstName = 'Bitte geben Sie Ihren Vornamen an';
    if (!contactInfo.lastName) errors.lastName = 'Bitte geben Sie Ihren Nachnamen an';
    if (!contactInfo.email) {
      errors.email = 'Bitte geben Sie Ihre E-Mail-Adresse an';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      errors.email = 'Ungültige E-Mail-Adresse';
    }
    if (!contactInfo.phone) {
      errors.phone = 'Bitte geben Sie Ihre Telefonnummer an';
    } else if (!/^[\d\s+()-]+$/.test(contactInfo.phone)) {
      errors.phone = 'Ungültige Telefonnummer';
    }
    if (!contactInfo.preferredContact) {
      errors.preferredContact = 'Bitte wählen Sie eine Kontaktmethode';
    }

    setContactInfoErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedEventType) {
      return;
    }
    if (currentStep === 2 && !validateEventDetails()) {
      return;
    }
    if (currentStep === 3 && !selectedPackage) {
      return;
    }
    if (currentStep === 4 && !validateContactInfo()) {
      return;
    }
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmissionError(null);

    try {
      // Prepare booking data for Supabase
      const bookingData: EventBooking = {
        event_type: selectedEventType as EventBooking['event_type'],
        event_date: eventDetails.date,
        event_time: eventDetails.time,
        duration: eventDetails.duration,
        guest_count: parseInt(eventDetails.guestCount),
        location_type: eventDetails.location,
        address: eventDetails.address,
        postal_code: eventDetails.postalCode,
        city: eventDetails.city,
        menu_package: selectedPackage as EventBooking['menu_package'],
        special_requests: specialRequests || undefined,
        dietary_needs: dietaryNeeds.length > 0 ? dietaryNeeds : undefined,
        first_name: contactInfo.firstName,
        last_name: contactInfo.lastName,
        email: contactInfo.email,
        phone: contactInfo.phone,
        company: contactInfo.company || undefined,
        preferred_contact: contactInfo.preferredContact,
        newsletter_subscribed: contactInfo.newsletter,
      };

      // Save to Supabase database only (no email)
      const { data, error } = await createEventBooking(bookingData);

      if (error) {
        setSubmissionError(error);
        setIsSubmitting(false);
        return;
      }

      if (data && data.reference_number) {
        setBookingReference(data.reference_number);
        setIsSubmitted(true);
        
        // Send admin notification email (non-blocking)
        try {
          const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
          const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
          
          await fetch(`${supabaseUrl}/functions/v1/send-admin-booking-notification`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseAnonKey}`,
            },
            body: JSON.stringify({ bookingData }),
          });
        } catch (emailError) {
          // Log error but don't block user experience
          console.error('Failed to send admin notification:', emailError);
        }
      }
    } catch (error: any) {
      console.error('Booking submission error:', error);
      setSubmissionError(
        'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventDetailsChange = (field: keyof EventDetails, value: string) => {
    setEventDetails({ ...eventDetails, [field]: value });
    if (eventDetailsErrors[field]) {
      setEventDetailsErrors({ ...eventDetailsErrors, [field]: '' });
    }
  };

  const handleContactInfoChange = (field: keyof ContactInfo, value: string | boolean) => {
    setContactInfo({ ...contactInfo, [field]: value });
    if (contactInfoErrors[field]) {
      setContactInfoErrors({ ...contactInfoErrors, [field]: '' });
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:238:6" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="238" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20flex%20items-center%20justify-center%22%7D" className="min-h-screen bg-background flex items-center justify-center">
        <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:239:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="239" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%22%7D" className="text-center">
          <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:240:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="240" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-16%20h-16%20border-4%20border-primary%20border-t-transparent%20rounded-full%20animate-spin%20mx-auto%20mb-4%22%7D" className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:241:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="241" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20font-body%22%2C%22textContent%22%3A%22L%C3%A4dt...%22%7D" className="text-muted-foreground font-body">Lädt...</p>
        </div>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:249:6" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="249" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20py-20%20px-6%22%7D" className="min-h-screen bg-background py-20 px-6">
        <ConfirmationMessage data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:250:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="250" data-component-file="BookingInteractive.tsx" data-component-name="ConfirmationMessage" data-component-content="%7B%22elementName%22%3A%22ConfirmationMessage%22%7D" bookingReference={bookingReference} email={contactInfo.email} />
      </div>
    );
  }

  return (
    <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:256:4" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="256" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20py-20%20px-6%22%7D" className="min-h-screen bg-background py-20 px-6">
      <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:257:6" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="257" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-5xl%20mx-auto%22%7D" className="max-w-5xl mx-auto">
        {/* Header */}
        <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:259:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="259" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22text-center%20mb-12%22%7D" className="text-center mb-12">
          <h1 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:260:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="260" data-component-file="BookingInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22font-headline%20text-4xl%20md%3Atext-5xl%20font-bold%20text-foreground%20mb-4%22%2C%22textContent%22%3A%22Buchen%20Sie%20Ihre%20Veranstaltung%22%7D" className="font-headline text-4xl md:text-5xl font-bold text-foreground mb-4">
            Buchen Sie Ihre Veranstaltung
          </h1>
          <p data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:263:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="263" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%20font-body%20max-w-2xl%20mx-auto%22%2C%22textContent%22%3A%22Folgen%20Sie%20unserem%20einfachen%20Buchungsprozess%2C%20um%20Ihre%20unvergessliche%20Pizza-Erfahrung%5Cn%20%20%20%20%20%20%20%20%20%20%20%20zu%20planen%22%7D" className="text-lg text-muted-foreground font-body max-w-2xl mx-auto">
            Folgen Sie unserem einfachen Buchungsprozess, um Ihre unvergessliche Pizza-Erfahrung
            zu planen
          </p>
        </div>

        {/* Step Indicator */}
        <BookingStepIndicator data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:270:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="270" data-component-file="BookingInteractive.tsx" data-component-name="BookingStepIndicator" data-component-content="%7B%22elementName%22%3A%22BookingStepIndicator%22%7D" currentStep={currentStep} steps={steps} />

        {/* Show submission error if exists */}
        {submissionError && (
          <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:274:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="274" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-destructive%2F10%20border%20border-destructive%20rounded-lg%20p-4%20mb-8%22%7D" className="bg-destructive/10 border border-destructive rounded-lg p-4 mb-8">
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:275:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="275" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-3%22%7D" className="flex items-start space-x-3">
              <Icon data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:276:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="276" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ExclamationTriangleIcon%22%2C%22className%22%3A%22text-destructive%20mt-0.5%22%7D" name="ExclamationTriangleIcon" size={20} className="text-destructive mt-0.5" />
              <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:277:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="277" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
                <p data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:278:16" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="278" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-destructive%20font-body%20font-semibold%22%2C%22textContent%22%3A%22Fehler%20bei%20der%20Buchung%22%7D" className="text-destructive font-body font-semibold">Fehler bei der Buchung</p>
                <p data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:279:16" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="279" data-component-file="BookingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-destructive%2F80%20font-body%20text-sm%20mt-1%22%7D" className="text-destructive/80 font-body text-sm mt-1">{submissionError}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form Content */}
        <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:286:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="286" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-lg%20shadow-warm%20p-8%20mb-8%22%7D" className="bg-card rounded-lg shadow-warm p-8 mb-8">
          {currentStep === 1 && (
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:288:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="288" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:289:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="289" data-component-file="BookingInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22W%C3%A4hlen%20Sie%20Ihre%20Veranstaltungsart%22%7D" className="font-headline text-2xl font-bold text-foreground mb-6">
                Wählen Sie Ihre Veranstaltungsart
              </h2>
              <EventTypeSelector data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:292:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="292" data-component-file="BookingInteractive.tsx" data-component-name="EventTypeSelector" data-component-content="%7B%22elementName%22%3A%22EventTypeSelector%22%7D"
                selectedType={selectedEventType}
                onSelect={setSelectedEventType}
              />
            </div>
          )}

          {currentStep === 2 && (
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:300:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="300" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:301:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="301" data-component-file="BookingInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Veranstaltungsdetails%22%7D" className="font-headline text-2xl font-bold text-foreground mb-6">
                Veranstaltungsdetails
              </h2>
              <EventDetailsForm data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:304:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="304" data-component-file="BookingInteractive.tsx" data-component-name="EventDetailsForm" data-component-content="%7B%22elementName%22%3A%22EventDetailsForm%22%7D"
                details={eventDetails}
                onChange={handleEventDetailsChange}
                errors={eventDetailsErrors}
              />
            </div>
          )}

          {currentStep === 3 && (
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:313:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="313" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:314:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="314" data-component-file="BookingInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Men%C3%BCauswahl%22%7D" className="font-headline text-2xl font-bold text-foreground mb-6">
                Menüauswahl
              </h2>
              <MenuSelectionForm data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:317:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="317" data-component-file="BookingInteractive.tsx" data-component-name="MenuSelectionForm" data-component-content="%7B%22elementName%22%3A%22MenuSelectionForm%22%7D"
                selectedPackage={selectedPackage}
                onPackageSelect={setSelectedPackage}
                specialRequests={specialRequests}
                onSpecialRequestsChange={setSpecialRequests}
                dietaryNeeds={dietaryNeeds}
                onDietaryNeedsChange={setDietaryNeeds}
              />
            </div>
          )}

          {currentStep === 4 && (
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:329:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="329" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:330:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="330" data-component-file="BookingInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Kontaktinformationen%22%7D" className="font-headline text-2xl font-bold text-foreground mb-6">
                Kontaktinformationen
              </h2>
              <ContactInformationForm data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:333:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="333" data-component-file="BookingInteractive.tsx" data-component-name="ContactInformationForm" data-component-content="%7B%22elementName%22%3A%22ContactInformationForm%22%7D"
                info={contactInfo}
                onChange={handleContactInfoChange}
                errors={contactInfoErrors}
              />
            </div>
          )}

          {currentStep === 5 && (
            <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:342:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="342" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
              <h2 data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:343:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="343" data-component-file="BookingInteractive.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-headline%20text-2xl%20font-bold%20text-foreground%20mb-6%22%2C%22textContent%22%3A%22Zusammenfassung%20Ihrer%20Buchung%22%7D" className="font-headline text-2xl font-bold text-foreground mb-6">
                Zusammenfassung Ihrer Buchung
              </h2>
              <BookingSummary data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:346:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="346" data-component-file="BookingInteractive.tsx" data-component-name="BookingSummary" data-component-content="%7B%22elementName%22%3A%22BookingSummary%22%7D"
                eventType={selectedEventType}
                eventDetails={eventDetails}
                selectedPackage={selectedPackage}
                dietaryNeeds={dietaryNeeds}
                contactInfo={contactInfo}
                specialRequests={specialRequests}
              />
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:359:8" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="359" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%22%7D" className="flex justify-between items-center">
          <button data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:360:10" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="360" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
            onClick={handleBack}
            disabled={currentStep === 1 || isSubmitting}
            className={`inline-flex items-center space-x-2 px-6 py-3 rounded-md font-body font-semibold transition-all duration-fast ${
              currentStep === 1 || isSubmitting
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-secondary hover:bg-secondary/90 text-secondary-foreground'
            }`}
          >
            <Icon data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:369:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="369" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronLeftIcon%22%7D" name="ChevronLeftIcon" size={20} />
            <span data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:370:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="370" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Zur%C3%BCck%22%7D">Zurück</span>
          </button>

          {currentStep < 5 ? (
            <button data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:374:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="374" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              onClick={handleNext}
              disabled={
                (currentStep === 1 && !selectedEventType) ||
                (currentStep === 3 && !selectedPackage) ||
                isSubmitting
              }
              className={`inline-flex items-center space-x-2 px-6 py-3 rounded-md font-body font-semibold transition-all duration-fast ${
                (currentStep === 1 && !selectedEventType) ||
                (currentStep === 3 && !selectedPackage) ||
                isSubmitting
                  ? 'bg-muted text-muted-foreground cursor-not-allowed'
                  : 'bg-conversion hover:bg-conversion/90 text-conversion-foreground shadow-cta'
              }`}
            >
              <span data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:389:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="389" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Weiter%22%7D">Weiter</span>
              <Icon data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:390:14" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="390" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronRightIcon%22%7D" name="ChevronRightIcon" size={20} />
            </button>
          ) : (
            <button data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:393:12" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="393" data-component-file="BookingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20space-x-2%20bg-success%20hover%3Abg-success%2F90%20text-success-foreground%20px-8%20py-3%20rounded-md%20font-body%20font-semibold%20shadow-cta%20transition-all%20duration-fast%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%22%7D"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="inline-flex items-center space-x-2 bg-success hover:bg-success/90 text-success-foreground px-8 py-3 rounded-md font-body font-semibold shadow-cta transition-all duration-fast disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:400:18" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="400" data-component-file="BookingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-5%20h-5%20border-2%20border-white%20border-t-transparent%20rounded-full%20animate-spin%22%7D" className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:401:18" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="401" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Wird%20gesendet...%22%7D">Wird gesendet...</span>
                </>
              ) : (
                <>
                  <Icon data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:405:18" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="405" data-component-file="BookingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckIcon%22%7D" name="CheckIcon" size={20} />
                  <span data-component-id="src/app/book-your-event/components/BookingInteractive.tsx:406:18" data-component-path="src/app/book-your-event/components/BookingInteractive.tsx" data-component-line="406" data-component-file="BookingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Buchung%20absenden%22%7D">Buchung absenden</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}